/**
 * The contract every Item renderer satisfies.
 *
 * Deliberately narrow. A renderer receives a label, a value, the options the Selection Policy
 * resolved, and a callback — it knows nothing about Liturgies, review states, or who is asking.
 * That is what lets the registry stay a plain lookup and lets a renderer be exercised on its own.
 *
 * `options` carries **every Rite-permitted option with its `offered` flag**, not a filtered list.
 * Dropping the withheld ones here would make an already-approved-but-withheld value unrenderable —
 * exactly the case a conflict has to be able to show (docs/engineering/06-selection-policy.md).
 */

import type { ItemType, ItemValue, OptionAvailability } from '@pius/contracts';

export interface ItemEditorProps {
  itemId: string;
  label: string;
  value: ItemValue | null;
  options: OptionAvailability[];
  /** True when the Liturgy is closed to planning, or the Policy makes this Item `read_only`. */
  disabled: boolean;
  minSelections?: number | undefined;
  maxSelections?: number | undefined;
  /**
   * The celebration's own timezone, for `date_time`. Passed in rather than read from the browser: a
   * secretary planning from another country must not shift a nuptial Mass because their laptop is
   * elsewhere.
   */
  timezone?: string | null | undefined;
  /**
   * The celebration's own People, for `person_ref`.
   *
   * A Person is chosen from this Liturgy's list rather than typed, so the recorded `person_id`
   * always resolves. **Choosing someone here grants them nothing** — it records who serves, and
   * authority comes only from a Capability (docs/engineering/04-authorization.md).
   */
  people?: { id: string; display_name: string }[] | undefined;
  /**
   * This celebration's attachments, for `attachment_ref`.
   *
   * **Only files the server reports as available may be chosen.** Referencing a quarantined file
   * from a Planning Item would put an unavailable attachment into an approved value, and later into
   * an export (docs/engineering/03-lifecycle-and-states.md).
   */
  attachments?: { id: string; display_name: string; available: boolean }[] | undefined;
  onChange: (next: ItemValue | null) => void;
}

export type ItemEditor = (props: ItemEditorProps) => React.JSX.Element;

/**
 * Why an option is in the state it is in.
 *
 * Built here rather than on the server: `decided_by` is structural and translatable, an English
 * sentence baked into a response is neither (docs/engineering/12-localization.md).
 */
export function attributionFor(option: OptionAvailability): string | null {
  if (option.offered) {
    // Only worth saying when a layer put it back. Offered-by-default needs no explanation.
    switch (option.decided_by) {
      case 'organization':
        return 'Restored by the organization';
      case 'cleric':
        return 'Restored by the presiding cleric';
      case 'liturgy':
        return 'Restored for this celebration';
      default:
        return null;
    }
  }

  switch (option.decided_by) {
    case 'rite':
      return 'Retired in this template';
    case 'organization':
      return 'Withheld by the organization';
    case 'cleric':
      return 'Withheld by the presiding cleric';
    case 'liturgy':
      return 'Withheld for this celebration';
    default:
      return 'Not offered';
  }
}

/**
 * Item types whose editing surface belongs to a later stage.
 *
 * Empty as of Stage 7: every type in `@pius/contracts` now has a real editor. Kept because the
 * registry's honesty depends on there being a place to name a deferral rather than fake one.
 */
export const DEFERRED_ITEM_TYPES: Partial<Record<ItemType, string>> = {};
